import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { media, HEADER_HEIGHT } from '../../styles/breakpoints';
import { font } from '../../styles/typography';
import { useIncomingCalls } from './IncomingCallProvider';
import IncomingCallToast from './IncomingCallToast';
import NoticeToast from './NoticeToast';

const Stack = styled.div`
  position: fixed;
  top: ${HEADER_HEIGHT + 2}px;
  right: 8px;
  z-index: 200; /* вище модульних сайдбарів (150) */
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 380px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
  & > * { pointer-events: auto; }

  ${media.mobile`
    top: ${HEADER_HEIGHT + 8}px;
    right: 8px;
    left: 8px;
    width: auto;
    max-width: none;
  `}
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px 4px 12px;
  border-radius: 999px;
  background: var(--spark-color-surface);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px var(--spark-color-shadow);
  ${font('sm')}
  color: var(--spark-color-text-control);
`;

const Pill = styled.button`
  ${font('base')}
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--spark-color-action-primary);
  background: var(--spark-color-action-primary);
  color: var(--spark-color-on-action-primary);
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  box-shadow: 0 6px 18px var(--spark-color-shadow-strong);
  &:hover { background: var(--spark-color-action-primary-hover); }
`;

const Count = styled.span`
  min-width: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--spark-color-surface);
  color: var(--spark-color-action-primary);
  font-weight: 700;
  text-align: center;
`;

const HideBtn = styled.button`
  ${font('sm')}
  border: 1px solid var(--spark-color-control-border);
  background: var(--spark-color-surface);
  color: var(--spark-color-text-control);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  &:hover { background: var(--spark-color-control-hover-surface); }
`;

/**
 * Стек сповіщень зі згортанням. Після переходу з тоста («Відкрити») решта згортається в кнопку
 * зі лічильником — щоб не заважали й до них можна було повернутись. Новий вхідний дзвінок
 * розгортає стек сам (його не можна пропустити); нові сповіщення згорнутий стек не розгортають.
 */
export default function IncomingCallStack() {
  const { calls, notices, dismissNotice } = useIncomingCalls();
  const [collapsed, setCollapsed] = useState(false);
  const seenCalls = useRef(new Set());
  const total = calls.length + notices.length;

  useEffect(() => {
    const fresh = calls.filter((c) => !seenCalls.current.has(c.callId));
    fresh.forEach((c) => seenCalls.current.add(c.callId));
    if (fresh.length) setCollapsed(false);
  }, [calls]);

  useEffect(() => {
    if (total === 0) setCollapsed(false);
  }, [total]);

  if (total === 0) return null;

  if (collapsed) {
    return (
      <Stack aria-live="polite">
        <Pill type="button" onClick={() => setCollapsed(false)} aria-label={`Показати сповіщення: ${total}`}>
          🔔 <Count>{total}</Count>
        </Pill>
      </Stack>
    );
  }

  const collapse = () => setCollapsed(true);
  return (
    <Stack aria-live="polite">
      <Bar>
        <span>Сповіщення: {total}</span>
        <HideBtn type="button" onClick={collapse}>Сховати</HideBtn>
      </Bar>
      {calls.map((call) => <IncomingCallToast key={call.callId} call={call} onOpen={collapse} />)}
      {notices.map((n) => <NoticeToast key={n.key} notice={n} onDismiss={dismissNotice} onOpen={collapse} />)}
    </Stack>
  );
}
